import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/DialogSheet';
import { useMediaQuery, useShallowEqualSelector } from '@/hooks';
import { usePlans } from '@/hooks/price';
import paymentModel from '@/models/payment';
import routes from '@/routes';
import { setIsPaymentBreakdownModalOpen } from '@/store/checkout';
import { clearBillingData } from '@/store/profile';
import { fetchBillingInfo } from '@/store/profile/thunks';
import { showError } from '@/utils';
import { formatPrice } from '@/utils/money';

import Button from '../Button';
import { Drawer, DrawerContent } from '../Drawer';
import ScrollArea from '../ScrollArea';
import Spinner from '../Spinner';
import Switch from '../Switch';
import Chip from './Chip';
import DetailCard from './DetailCard';

function PaymentBreakdownModal() {
	const open = useSelector(
		state => state.checkout.isPaymentBreakdownModalOpen
	);

	const { subscriptionInfo } = useShallowEqualSelector(state => ({
		subscriptionInfo: state.profile.subscriptionInfo
	}));

	const billingInfoCalledRef = useRef(false);

	const [addService, setAddService] = useState(true);
	const [serviceLoading, setServiceLoading] = useState(false);

	const loggedIn = useSelector(state => state.profile.loggedIn);

	const plansData = usePlans();

	const { trialPlan, servicePlan } = plansData;

	const mdDown = useMediaQuery('mdDown');

	const dispatch = useDispatch();
	const { t } = useTranslation();

	const isFastCheckout = !!useMatch(routes.fastCheckout);

	function handleClose() {
		dispatch(setIsPaymentBreakdownModalOpen(false));
	}

	async function handleContinue() {
		try {
			if (addService) {
				setServiceLoading(true);

				await paymentModel.addServiceSubscription();
			}
		} catch (error) {
			showError(error);
		} finally {
			setServiceLoading(false);
			handleClose();
		}
	}

	const textHeader = t('paymentBreakdown.header');

	const planModules = t('paymentBreakdown.planModules', {
		returnObjects: true
	});

	useEffect(() => {
		(async () => {
			try {
				if (
					!open ||
					subscriptionInfo ||
					billingInfoCalledRef?.current ||
					isFastCheckout
				) {
					return;
				}

				billingInfoCalledRef.current = true;

				const response = await dispatch(fetchBillingInfo()).unwrap();

				if (!response?.subscription) {
					handleClose();
				}
			} catch (error) {
				showError(error);
			}
		})();

		return () => {
			dispatch(clearBillingData());
		};
	}, [open]);

	const content = (
		<>
			<DialogTop title={textHeader} />

			{trialPlan ? (
				<div className="flex w-full flex-col pl-4 pr-2 md:flex-row">
					<ScrollArea
						className="h-[calc(90svh - 260px)] md:h-[calc(80svh - 260px)] h-full w-full pr-4"
						maxHeight={mdDown ? '355px' : '312px'}
					>
						<div className="flex w-full flex-col gap-5 py-5">
							<div className="flex flex-col gap-4">
								<p className="text-md block max-w-[375px] font-semibold leading-[28px] text-black md:max-w-[initial]">
									{t('paymentBreakdown.title')}
								</p>

								<div className="flex flex-col gap-3 md:gap-[25px]">
									<DetailCard
										{...planModules.trial}
										priceText={formatPrice({
											amount: trialPlan?.baseAmount,
											currency: trialPlan.currency
										})}
										planKey="trial"
										footerComponent={
											subscriptionInfo ? (
												<Chip text={t('paymentBreakdown.paid')} />
											) : null
										}
									/>

									<DetailCard
										{...planModules.service}
										priceText={formatPrice({
											amount: servicePlan?.baseAmount,
											currency: servicePlan?.currency
										})}
										planKey="service"
										footerComponent={
											<div className="flex items-center gap-2">
												<Switch
													id="add-service-switch"
													checked={addService}
													onCheckedChange={checked =>
														setAddService(checked)
													}
												/>
												<label
													htmlFor="add-service-switch"
													className="text-xs font-semibold"
												>
													{t('paymentBreakdown.added')}
												</label>
											</div>
										}
									/>
								</div>
							</div>
						</div>
					</ScrollArea>
				</div>
			) : (
				<div className="flex h-full min-h-[200px] w-full items-center justify-center">
					<Spinner className="mx-auto" size={50} />
				</div>
			)}

			<DialogFooter singleButton>
				<Button
					className="w-full"
					loading={serviceLoading}
					onClick={handleContinue}
				>
					{t('common.continue')}
				</Button>
			</DialogFooter>
		</>
	);

	const isOpen = open && loggedIn && !isFastCheckout && subscriptionInfo;

	return mdDown ? (
		<Drawer open={isOpen} onOpenChange={handleClose}>
			<DrawerContent
				onClose={handleClose}
				onOpenAutoFocus={event => event.preventDefault()}
				className="w-full focus-visible:outline-none"
				onPointerDownOutside={event => {
					event.preventDefault();
				}}
			>
				{content}
			</DrawerContent>
		</Drawer>
	) : (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent
				className="w-[calc(100vw-40px)] max-w-[700px]"
				onClose={handleClose}
				hasCross={false}
				onOpenAutoFocus={event => event.preventDefault()}
				onPointerDownOutside={event => {
					event.preventDefault();
				}}
			>
				{content}
			</DialogContent>
		</Dialog>
	);
}

export default PaymentBreakdownModal;
